import { PanelLeft, Search } from 'lucide-react';
import { SearchGroup } from '../index-page/SearchGroup';
import { Tooltip } from '../Tooltip';
import { ProfileRailContext, useProfileRailCollapsed } from './profileRail';
import { ProfileSectionTitleContext } from './profileSectionTitle';
import type { ReactNode } from 'react';

/**
 * The sidebar profile layout — identity and navigation on the left, one panel of content on
 * the right.
 *
 * This is the first of the product's two canonical profile shapes. Use it when a record has
 * more about it than one screen holds, so the content splits into panels the reader picks
 * between: an account, a person, a case file. The other shape — header on top, content
 * below — suits a record whose content is a single continuous thing, typically one table.
 *
 * Both columns scroll independently. The page itself never scrolls: the shell is
 * height-capped and each side owns its own overflow, so the identity block stays put while
 * a long panel scrolls beside it.
 *
 * DELIBERATE DEVIATION from reference §7, recorded in CLAUDE.md. §7 describes a single
 * stacked layout (breadcrumb → identity banner → toolbar → status banner → content); the
 * product uses these two shapes instead.
 */
export function ProfileShell({
  header,
  sectionTitle,
  sidebar,
  banner,
  children,
  id,
  fill = false,
  railCollapsible = false,
  railSearch,
}: {
  /**
   * A full-width identity strip above both columns — `<ProfileHeader>`.
   *
   * **Optional, and it MOVES the identity rather than repeating it.** A header carrying the
   * avatar, the name and the status beside a sidebar carrying the same three is one record
   * printed twice on one screen. A page that passes this drops `<ProfileIdentity>`.
   *
   * **The sidebar is then the panel picker, which `<ProfileFacts>` may LEAD.** That block is a
   * list of record facts, not a second identity — the 201 file's rail deliberately carries no
   * name row for that reason. It does repeat the unit and the assignment the strip above also
   * shows, by a product call on 27 Oct 2026 for a self-contained rail; it is the ONE page that
   * repeats anything, it is recorded in `.claude/rules/personnel.md`, and a second one is a
   * decision made out loud rather than a precedent to copy.
   */
  header?: ReactNode;
  /**
   * The open panel’s name, which every `<ProfileSection>` beneath this shell heads itself
   * with. Resolve it from the nav with `findNavLabel` rather than typing it: the rail and
   * the heading are one fact, and a second copy is one a rename leaves behind.
   */
  sectionTitle?: string;
  /** The left column: identity block (or, with a header, just the nav) and actions. */
  sidebar: ReactNode;
  /**
   * Record-level state, pinned above the panel content and shown on every panel — a lockout
   * or a suspension is true of the whole record, so it must not be something the reader
   * only discovers by opening the right tab.
   */
  banner?: ReactNode;
  /** The right column: the selected panel. */
  children: ReactNode;
  id?: string;
  /**
   * The open panel OWNS its region — the column stops scrolling and hands the panel its
   * height, and the readable-measure cap comes off so it takes the column's whole WIDTH. For a
   * panel whose folder tabs stand over tables that scroll inside them (Leaves): left to the
   * column, the tabs scrolled away with the rows, and at 896px the ledger scrolled sideways.
   * The cap exists for label/value cards; a table owns its own layout.
   */
  fill?: boolean;
  /**
   * The rail carries a MINIMIZE button, and the reader's choice is remembered.
   *
   * Opt-in, because a collapsed rail still has to work: everything the sidebar draws needs an
   * icon-width form or an honest reason to disappear. `<ProfileFacts>` hides — the facts it
   * carries are repeated on the header strip, which is the whole reason the 201 file was
   * allowed to repeat them — and `<ProfileNav>` draws its top level as glyphs. A page putting
   * `<ProfileIdentity>` in the rail has no such form for it yet, so it does not pass this.
   *
   * It REVERSES "the rail has no collapse at all, a fixed `w-72`" — a product call on
   * 27 Oct 2026 for the 201 file, whose panels are wide tables that want the room.
   */
  railCollapsible?: boolean;
  /**
   * A search over the whole RECORD, in the rail above the panel picker.
   *
   * **A typed prop, never a `ReactNode` slot.** Handing the shell a node would let every
   * page draw its own strip, which is the `onCreate` defect this product has already paid
   * for once — nineteen hand-rolled copies of one button. The shell draws the one
   * `<SearchGroup>`; the page owns the term and what the results mean.
   *
   * **It shares the row with the minimize toggle** (03 Nov 2026), because they are one
   * band of rail chrome: two rows of controls over a picker is a header taller than the
   * first few entries it introduces. Collapsed, the field has no icon-width form, so the
   * rail draws a search GLYPH that expands it — the same answer `<ProfileNav>` gives a
   * collapsed group, rather than a flyout nobody can type into.
   */
  railSearch?: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    /** Matching ROWS, in the group's own count segment. Omit it and none is drawn. */
    total?: number;
  };
}) {
  const { collapsed: railCollapsed, toggle: toggleRail, expand: expandRail } =
    useProfileRailCollapsed();

  const collapsed = railCollapsible && railCollapsed;

  /*
    ONE glyph in both states, as the app rail's own toggle settled on: a button whose icon
    changes under the cursor reads as two different controls, and the tooltip and the
    `aria-label` already say which way this one will go.

    A SEGMENT of the search group where there is one, and a standalone glyph where there is
    not. Product call, 03 Nov 2026: the two were adjacent controls doing one job — narrow the
    rail, or search what is in it — and a boxed field with a loose button beside it read as a
    strip that had come apart. Inside the group it is the same trailing segment the index
    toolbar's cog and the filter rail's own already are, so the rail borrows a shape the
    reader has met rather than inventing a third.

    The two paints are not interchangeable: a segment draws the group's own divider and takes
    the right radius from it, and a loose button needs its own rounding and hover ground.
  */
  const toggleButton = (inGroup: boolean) => (
    <Tooltip label={collapsed ? 'Expand sidebar' : 'Minimize sidebar'}>
      <button
        type="button"
        id={id ? `${id}-rail-toggle` : undefined}
        onClick={toggleRail}
        aria-expanded={!collapsed}
        aria-controls={id ? `${id}-rail` : undefined}
        aria-label={collapsed ? 'Expand sidebar' : 'Minimize sidebar'}
        className={
          inGroup
            ? [
                'flex shrink-0 items-center rounded-r-lg border-l border-slate-300 px-3 text-slate-500',
                'transition hover:bg-slate-50 hover:text-slate-700 focus:outline-none',
                'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-200',
              ].join(' ')
            : [
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400',
                'transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none',
                'focus-visible:ring-2 focus-visible:ring-brand-300',
              ].join(' ')
        }
      >
        <PanelLeft className="h-4 w-4" aria-hidden="true" />
      </button>
    </Tooltip>
  );

  /*
    The shared group, boxed rather than `flush`: it sits INSIDE the rail with the facts and
    the picker under it, where the filter rail's own header sits ON the rail's edge. The
    count segment is the page's to supply — it is a number of MATCHES, and only the page
    knows what it searched.
  */
  const searchField = railSearch ? (
    <SearchGroup
      id={id ? `${id}-rail` : 'profile-rail'}
      className="min-w-0 flex-1"
      value={railSearch.value}
      onChange={railSearch.onChange}
      placeholder={railSearch.placeholder}
      total={railSearch.total}

      // MATCHES, never records: this searches inside ONE record, so a count of `records`
      // would report a number of files that has nothing to do with what was found.
      unit={{ one: 'match', many: 'matches' }}
    >
      {/* The rail's only other control, as a segment rather than a neighbour. */}
      {railCollapsible ? toggleButton(true) : null}
    </SearchGroup>
  ) : null;

  /*
    Collapsed, there is no field to draw — a text input has no icon-width form. The glyph
    EXPANDS the rail, which is what a collapsed group does too, so the reader learns one
    behaviour rather than two. Focus is left to the expansion: the field mounts on the next
    render, and reaching into it from here would be a ref into a component that does not
    exist yet.
  */
  const searchGlyph = (
    <Tooltip label="Search this record">
      <button
        type="button"
        id={id ? `${id}-rail-search-expand` : undefined}
        onClick={expandRail}
        aria-label="Search this record"
        className={[
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400',
          'transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none',
          'focus-visible:ring-2 focus-visible:ring-brand-300',
        ].join(' ')}
      >
        <Search className="h-4 w-4" aria-hidden="true" />
      </button>
    </Tooltip>
  );

  return (
    <ProfileSectionTitleContext.Provider value={sectionTitle}>
    <ProfileRailContext.Provider value={{ collapsed, expand: expandRail }}>
    <div id={id} className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      {header}

      {/* The two columns, under whatever the header took. `min-h-0` again, or the row
          grows past the shell instead of scrolling inside it. */}
      <div className="flex min-h-0 min-w-0 flex-1">
        {/*
          The rail animates its WIDTH rather than swapping between two layouts, so minimizing
          reads as one movement — the app rail's own call, kept here so the two behave alike.
          `w-16` is its collapsed width too: one measurement, twice.
        */}
        <aside
          id={id ? `${id}-rail` : undefined}
          aria-label="Record"
          data-collapsed={collapsed}
          className={[
            'modern-scrollbar flex shrink-0 flex-col overflow-y-auto border-r border-slate-200',
            'transition-[width] duration-200 ease-out motion-reduce:transition-none',
            collapsed ? 'w-16' : 'w-72',
          ].join(' ')}
        >
          {railCollapsible || railSearch ? (
            /*
              ONE row of rail chrome: the search, then the toggle at the far end, opposite the
              content it folds away. Collapsed there is nothing to sit beside, so the column
              centres and stacks. It draws no rule of its own — the block under it owns the
              first seam, and two lines there would separate nothing.
            */
            <div
              className={`flex shrink-0 gap-1 px-2 pt-2 ${
                collapsed
                  ? 'flex-col items-center'
                  : 'items-center justify-end'
              }`}
            >
              {railSearch ? (collapsed ? searchGlyph : searchField) : null}
              {/*
                Expanded WITH a search, the toggle is already inside the group. It stands on
                its own only where there is no group to join — a collapsed rail, or a page
                that minimizes and does not search.
              */}
              {railCollapsible && (collapsed || !railSearch) ? toggleButton(false) : null}
            </div>
          ) : null}

          {sidebar}
        </aside>

        {/* min-w-0 is load-bearing: without it a wide panel pushes the sidebar off-screen
          instead of scrolling inside its own container. */}
        <main
          className={`modern-scrollbar min-h-0 min-w-0 flex-1 bg-slate-50/60 ${
            fill ? 'overflow-hidden' : 'overflow-y-auto'
          }`}
        >
          {/*
          Cards are capped to a readable measure and centred rather than stretched to the
          viewport. A label/value table run out to 1600px puts its value column a screen away
          from its label, and the eye loses the row on the way across.
        */}
          <div
            className={`mx-auto flex min-w-0 flex-col gap-5 p-5 ${
              fill ? 'h-full min-h-0' : 'max-w-4xl'
            }`}
          >
            {banner}
            {children}
          </div>
        </main>
      </div>
    </div>
    </ProfileRailContext.Provider>
    </ProfileSectionTitleContext.Provider>
  );
}
